import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLocation } from 'react-router-dom';
import SuperAdminNav from '../../components/SuperAdminNav';

const AGE_RANGES = [
  { label: 'Under 10', min: 0, max: 9 },
  { label: '10\u201313', min: 10, max: 13 },
  { label: '14+', min: 14, max: 99 },
];

const emptyQuestion = () => ({
  question_text: '',
  input_type: 'text',
  point_value: '1',
  options: [],
  acceptable_answers: [],
  min_char_count: null,
});

export default function Lessons() {
  const { apiFetch } = useAuth();
  const location = useLocation();
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    date: '', title: '', description: '', youtube_url: '', target_time: '',
    completion_points: '10', extra_points: '5', bonus_criteria: '',
    selectedAgeRanges: [],
  });
  const [pointsLocked, setPointsLocked] = useState(false);
  const [hasQuestions, setHasQuestions] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showQueue, setShowQueue] = useState(false);
  const [queue, setQueue] = useState(null);
  const [filterAge, setFilterAge] = useState('all');
  const [autoFilling, setAutoFilling] = useState(false);

  const loadLessons = useCallback(async () => {
    try {
      const params = filterAge !== 'all' ? `?age_range=${filterAge}` : '';
      const data = await apiFetch(`/api/super/lessons${params}`);
      setLessons(data.lessons);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [apiFetch, filterAge]);

  useEffect(() => { loadLessons(); }, [loadLessons]);

  // Handle prefill from Library page
  useEffect(() => {
    if (location.state?.prefill) {
      const { title, description, youtube_url } = location.state.prefill;
      setForm(f => ({ ...f, title: title || '', description: description || '', youtube_url: youtube_url || '' }));
      setEditing(null);
      setHasQuestions(false);
      setQuestions([]);
      setShowModal(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const loadQueue = async () => {
    try {
      const data = await apiFetch('/api/super/lessons/queue');
      setQueue(data.queue);
      setShowQueue(true);
    } catch (err) { console.error(err); }
  };

  const handleAutoFill = async () => {
    if (!window.confirm('Auto-fill lessons for the next 21 days from the library?')) return;
    setAutoFilling(true);
    try {
      const data = await apiFetch('/api/super/lessons/auto-fill', { method: 'POST' });
      alert(data.message || `Created ${data.created} lesson(s)`);
      loadLessons();
    } catch (err) {
      console.error(err);
      alert('Auto-fill failed: ' + (err.message || 'Unknown error'));
    } finally {
      setAutoFilling(false);
    }
  };

  const checkDatePoints = async (date) => {
    if (!date) return;
    try {
      const data = await apiFetch(`/api/super/lessons/date-points/${date}`);
      if (data.locked) {
        setPointsLocked(true);
        setForm(f => ({ ...f, completion_points: String(data.completion_points), extra_points: String(data.extra_points) }));
      } else {
        setPointsLocked(false);
      }
    } catch (err) { console.error(err); }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({
      date: '', title: '', description: '', youtube_url: '', target_time: '',
      completion_points: '10', extra_points: '5', bonus_criteria: '',
      selectedAgeRanges: [],
    });
    setPointsLocked(false);
    setHasQuestions(false);
    setQuestions([]);
    setShowModal(true);
  };

  const openEdit = async (lesson) => {
    setEditing(lesson);
    const selectedAgeRanges = (lesson.age_ranges || []).map(ar =>
      `${ar.age_range_min}-${ar.age_range_max}`
    );
    setForm({
      date: (lesson.date || '').split('T')[0],
      title: lesson.title,
      description: lesson.description || '',
      youtube_url: lesson.youtube_url || '',
      target_time: lesson.target_time || '',
      completion_points: String(lesson.completion_points != null ? lesson.completion_points : 10),
      extra_points: String(lesson.extra_points != null ? lesson.extra_points : 5),
      bonus_criteria: lesson.bonus_criteria || '',
      selectedAgeRanges,
    });
    setPointsLocked(false);
    // Load questions
    try {
      const data = await apiFetch(`/api/super/lessons/${lesson.id}/questions`);
      if (data.questions && data.questions.length > 0) {
        setHasQuestions(true);
        setQuestions(data.questions.map(q => ({
          id: q.id,
          question_text: q.question_text,
          input_type: q.input_type,
          point_value: String(q.point_value),
          options: q.options || [],
          acceptable_answers: q.acceptable_answers || [],
          min_char_count: q.min_char_count || null,
        })));
      } else {
        setHasQuestions(false);
        setQuestions([]);
      }
    } catch (err) {
      setHasQuestions(false);
      setQuestions([]);
    }
    setShowModal(true);
  };

  const toggleAgeRange = (key) => {
    setForm(f => {
      const selected = f.selectedAgeRanges.includes(key)
        ? f.selectedAgeRanges.filter(k => k !== key)
        : [...f.selectedAgeRanges, key];
      return { ...f, selectedAgeRanges: selected };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (form.selectedAgeRanges.length === 0) {
      alert('Please select at least one age range');
      return;
    }
    setSaving(true);
    try {
      const age_ranges = form.selectedAgeRanges.map(key => {
        const [min, max] = key.split('-').map(Number);
        return { age_range_min: min, age_range_max: max };
      });
      const payload = {
        date: form.date,
        title: form.title,
        description: form.description,
        youtube_url: form.youtube_url,
        target_time: form.target_time,
        completion_points: form.completion_points,
        extra_points: form.extra_points,
        bonus_criteria: form.bonus_criteria,
        age_ranges,
      };

      let lessonId;
      if (editing) {
        const result = await apiFetch(`/api/super/lessons/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        lessonId = result.lesson.id;
      } else {
        const result = await apiFetch('/api/super/lessons', { method: 'POST', body: JSON.stringify(payload) });
        lessonId = result.lesson.id;
      }

      // Save questions
      const questionsToSave = hasQuestions ? questions : [];
      await apiFetch(`/api/super/lessons/${lessonId}/questions`, {
        method: 'PUT',
        body: JSON.stringify({ questions: questionsToSave }),
      });

      setShowModal(false);
      loadLessons();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this lesson? Completions will also be removed.')) return;
    try {
      await apiFetch(`/api/super/lessons/${id}`, { method: 'DELETE' });
      loadLessons();
    } catch (err) { alert(err.message); }
  };

  // Question helpers (same pattern as Drills.js)
  const addQuestion = () => {
    if (questions.length >= 20) return;
    setQuestions([...questions, emptyQuestion()]);
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'input_type') {
      if (value === 'text') {
        updated[index].options = [];
        if (updated[index].acceptable_answers.length === 0) updated[index].acceptable_answers = [];
      } else {
        updated[index].acceptable_answers = [];
        if (updated[index].options.length === 0) updated[index].options = [];
      }
    }
    setQuestions(updated);
  };

  const removeQuestion = (index) => setQuestions(questions.filter((_, i) => i !== index));

  const addOption = (qIndex) => {
    const updated = [...questions];
    updated[qIndex] = { ...updated[qIndex], options: [...updated[qIndex].options, { option_text: '', is_correct: false }] };
    setQuestions(updated);
  };

  const updateOption = (qIndex, oIndex, field, value) => {
    const updated = [...questions];
    const opts = [...updated[qIndex].options];
    opts[oIndex] = { ...opts[oIndex], [field]: value };
    if (field === 'is_correct' && value && updated[qIndex].input_type === 'radio') {
      opts.forEach((o, i) => { if (i !== oIndex) opts[i] = { ...o, is_correct: false }; });
    }
    updated[qIndex] = { ...updated[qIndex], options: opts };
    setQuestions(updated);
  };

  const removeOption = (qIndex, oIndex) => {
    const updated = [...questions];
    updated[qIndex] = { ...updated[qIndex], options: updated[qIndex].options.filter((_, i) => i !== oIndex) };
    setQuestions(updated);
  };

  const addAcceptableAnswer = (qIndex) => {
    const updated = [...questions];
    updated[qIndex] = { ...updated[qIndex], acceptable_answers: [...updated[qIndex].acceptable_answers, { answer_text: '' }] };
    setQuestions(updated);
  };

  const updateAcceptableAnswer = (qIndex, aIndex, value) => {
    const updated = [...questions];
    const answers = [...updated[qIndex].acceptable_answers];
    answers[aIndex] = { ...answers[aIndex], answer_text: value };
    updated[qIndex] = { ...updated[qIndex], acceptable_answers: answers };
    setQuestions(updated);
  };

  const removeAcceptableAnswer = (qIndex, aIndex) => {
    const updated = [...questions];
    updated[qIndex] = { ...updated[qIndex], acceptable_answers: updated[qIndex].acceptable_answers.filter((_, i) => i !== aIndex) };
    setQuestions(updated);
  };

  const formatDate = (dateStr) => {
    const d = new Date((dateStr || '').split('T')[0] + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const ageRangeLabel = (ar) => {
    if (ar.age_range_max <= 9) return 'Under 10';
    if (ar.age_range_min === 10 && ar.age_range_max === 13) return '10\u201313';
    if (ar.age_range_min >= 14) return '14+';
    return `${ar.age_range_min}\u2013${ar.age_range_max}`;
  };

  if (loading) return <div className="admin-layout"><SuperAdminNav activeItem="lessons" /><div className="admin-page"><div className="loading"><div className="spinner" /></div></div></div>;

  return (
    <div className="admin-layout">
    <SuperAdminNav activeItem="lessons" />
    <div className="admin-page">
      <div className="flex-between mb-16">
        <h1 className="page-title" style={{ marginBottom: 0 }}>Lessons</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-outline btn-sm" onClick={loadQueue}>Queue</button>
          <button className="btn btn-blue btn-sm" onClick={handleAutoFill} disabled={autoFilling}>
            {autoFilling ? 'Filling...' : 'Auto-fill'}
          </button>
          <button className="btn btn-orange btn-sm" onClick={openAdd}>+ Add Lesson</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {['all', 'under10', '10-13', '14+'].map(f => (
          <button key={f} className={`btn btn-sm ${filterAge === f ? 'btn-orange' : 'btn-outline'}`}
            onClick={() => setFilterAge(f)}>
            {f === 'all' ? 'All' : f === 'under10' ? 'Under 10' : f}
          </button>
        ))}
      </div>

      {lessons.map((l) => (
        <div key={l.id} className="drill-row">
          <div className="drill-row-header">
            <span className="drill-row-date">{formatDate(l.date)}</span>
            <span style={{ display: 'flex', gap: 4, marginLeft: 8 }}>
              {l.auto_assigned && <span className="age-range-tag" style={{ background: '#333', color: '#999' }}>Auto</span>}
              {(l.age_ranges || []).map((ar, i) => (
                <span key={i} className="age-range-tag">{ageRangeLabel(ar)}</span>
              ))}
            </span>
          </div>
          <div className="drill-row-title">{l.title}</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>
            {l.completion_points}pts + {l.extra_points}pts bonus
            {l.question_count > 0 && ` | ${l.question_count} question${l.question_count !== 1 ? 's' : ''}`}
          </div>
          <div className="drill-row-actions">
            <button className="btn btn-outline btn-sm" onClick={() => openEdit(l)}>Edit</button>
            <button className="btn btn-danger btn-sm" onClick={() => handleDelete(l.id)}>Delete</button>
          </div>
        </div>
      ))}

      {lessons.length === 0 && <div className="no-season-msg">No lessons yet.</div>}

      {/* Queue Modal */}
      {showQueue && queue && (
        <div className="modal-overlay" onClick={() => setShowQueue(false)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <h2>Upcoming Lesson Queue</h2>
            <div className="lesson-queue">
              {['under10', '10-13', '14+'].map(key => (
                <div key={key} className="lesson-queue-column">
                  <div className="lesson-queue-title">
                    {key === 'under10' ? 'Under 10' : key}
                  </div>
                  {(queue[key] || []).length === 0 ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: 8 }}>No upcoming lessons</div>
                  ) : (
                    (queue[key] || []).map(l => (
                      <div key={l.id} className="lesson-queue-item">
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{formatDate(l.date)}</div>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{l.title}</div>
                      </div>
                    ))
                  )}
                </div>
              ))}
            </div>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setShowQueue(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <h2>{editing ? 'Edit Lesson' : 'Add Lesson'}</h2>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label className="form-label">Date</label>
                <input className="form-input" type="date" value={form.date}
                  onChange={(e) => { setForm({...form, date: e.target.value}); checkDatePoints(e.target.value); }}
                  required />
              </div>
              <div className="form-group">
                <label className="form-label">Title</label>
                <input className="form-input" value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} required />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-textarea" value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">YouTube URL</label>
                <input className="form-input" value={form.youtube_url} onChange={(e) => setForm({...form, youtube_url: e.target.value})} placeholder="https://youtube.com/watch?v=..." />
              </div>
              <div className="form-group">
                <label className="form-label">Target Time (minutes)</label>
                <input className="form-input" type="number" min="1" value={form.target_time} onChange={(e) => setForm({...form, target_time: e.target.value})} placeholder="e.g. 15" />
              </div>

              {/* Age Range Checkboxes */}
              <div className="form-group">
                <label className="form-label">Age Ranges</label>
                <div className="age-range-checkboxes">
                  {AGE_RANGES.map(ar => {
                    const key = `${ar.min}-${ar.max}`;
                    const selected = form.selectedAgeRanges.includes(key);
                    return (
                      <button key={key} type="button"
                        className={`age-range-checkbox ${selected ? 'selected' : ''}`}
                        onClick={() => toggleAgeRange(key)}>
                        {ar.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="form-row" style={{ alignItems: 'flex-start' }}>
                <div className="form-group form-group-half">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    Points: Completion
                    {pointsLocked && <span className="point-locked-indicator">Locked</span>}
                  </label>
                  <input className="form-input" type="number" min="0" value={form.completion_points}
                    onChange={(e) => setForm({...form, completion_points: e.target.value})}
                    disabled={pointsLocked && !editing} />
                </div>
                <div className="form-group form-group-half">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    Bonus Points
                    {pointsLocked && <span className="point-locked-indicator">Locked</span>}
                  </label>
                  <input className="form-input" type="number" min="0" value={form.extra_points}
                    onChange={(e) => setForm({...form, extra_points: e.target.value})}
                    disabled={pointsLocked && !editing} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Bonus Points Criteria</label>
                <input className="form-input" value={form.bonus_criteria} onChange={(e) => setForm({...form, bonus_criteria: e.target.value})} placeholder="e.g. I did 15+ extra minutes of training" />
              </div>

              {/* Questions Toggle */}
              <label className="form-checkbox">
                <input type="checkbox" checked={hasQuestions} onChange={(e) => {
                  setHasQuestions(e.target.checked);
                  if (e.target.checked && questions.length === 0) setQuestions([emptyQuestion()]);
                }} />
                <span>Add Questions?</span>
              </label>

              {/* Question Builder */}
              {hasQuestions && (
                <div className="question-builder">
                  {questions.map((q, qi) => (
                    <div key={qi} className="question-card">
                      <div className="question-header">
                        <span className="question-number">Question {qi + 1}</span>
                        <button type="button" className="btn-remove" onClick={() => removeQuestion(qi)}>Remove</button>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Question Text</label>
                        <input className="form-input" value={q.question_text} onChange={(e) => updateQuestion(qi, 'question_text', e.target.value)} placeholder="Enter your question..." required />
                      </div>
                      <div className="form-row">
                        <div className="form-group form-group-half">
                          <label className="form-label">Input Type</label>
                          <select className="form-input" value={q.input_type} onChange={(e) => updateQuestion(qi, 'input_type', e.target.value)}>
                            <option value="text">Text Box</option>
                            <option value="radio">Radio Buttons</option>
                            <option value="checkbox">Checkboxes</option>
                          </select>
                        </div>
                        <div className="form-group form-group-half">
                          <label className="form-label">{q.input_type === 'checkbox' ? 'Points Per Correct' : 'Point Value'}</label>
                          <input className="form-input" type="number" min="1" value={q.point_value} onChange={(e) => updateQuestion(qi, 'point_value', e.target.value)} />
                        </div>
                      </div>

                      {q.input_type === 'text' && (
                        <div className="question-answers-section">
                          <label className="form-checkbox" style={{ marginBottom: 8 }}>
                            <input type="checkbox" checked={q.min_char_count != null} onChange={(e) => {
                              updateQuestion(qi, 'min_char_count', e.target.checked ? 50 : null);
                            }} />
                            <span>Allow any answer with a minimum character count</span>
                          </label>
                          {q.min_char_count != null ? (
                            <div className="form-group" style={{ marginTop: 4 }}>
                              <label className="form-label">Minimum Characters</label>
                              <input className="form-input" type="number" min="1" value={q.min_char_count} onChange={(e) => updateQuestion(qi, 'min_char_count', parseInt(e.target.value, 10) || 1)} style={{ width: 120 }} />
                            </div>
                          ) : (
                            <>
                              <label className="form-label">Acceptable Answers</label>
                              <div className="question-warning">Add every reasonable variation. Players must match one exactly (case-insensitive).</div>
                              {q.acceptable_answers.map((a, ai) => (
                                <div key={ai} className="option-row">
                                  <input className="form-input" value={a.answer_text} onChange={(e) => updateAcceptableAnswer(qi, ai, e.target.value)} placeholder="Acceptable answer..." />
                                  <button type="button" className="btn-remove-sm" onClick={() => removeAcceptableAnswer(qi, ai)}>x</button>
                                </div>
                              ))}
                              <button type="button" className="btn btn-outline btn-sm add-option-btn" onClick={() => addAcceptableAnswer(qi)}>+ Add Answer</button>
                            </>
                          )}
                        </div>
                      )}

                      {q.input_type === 'radio' && (
                        <div className="question-answers-section">
                          <label className="form-label">Answer Options</label>
                          {q.options.map((o, oi) => (
                            <div key={oi} className="option-row">
                              <label className="option-correct-label" title="Correct answer">
                                <input type="radio" name={`q${qi}_correct`} checked={o.is_correct} onChange={() => updateOption(qi, oi, 'is_correct', true)} />
                              </label>
                              <input className="form-input" value={o.option_text} onChange={(e) => updateOption(qi, oi, 'option_text', e.target.value)} placeholder="Option text..." />
                              <button type="button" className="btn-remove-sm" onClick={() => removeOption(qi, oi)}>x</button>
                            </div>
                          ))}
                          <button type="button" className="btn btn-outline btn-sm add-option-btn" onClick={() => addOption(qi)}>+ Add Option</button>
                        </div>
                      )}

                      {q.input_type === 'checkbox' && (
                        <div className="question-answers-section">
                          <label className="form-label">Answer Options</label>
                          <div className="question-warning">Players earn points for each correct answer they select.</div>
                          {q.options.map((o, oi) => (
                            <div key={oi} className="option-row">
                              <label className="option-correct-label" title="Correct answer">
                                <input type="checkbox" checked={o.is_correct} onChange={(e) => updateOption(qi, oi, 'is_correct', e.target.checked)} />
                              </label>
                              <input className="form-input" value={o.option_text} onChange={(e) => updateOption(qi, oi, 'option_text', e.target.value)} placeholder="Option text..." />
                              <button type="button" className="btn-remove-sm" onClick={() => removeOption(qi, oi)}>x</button>
                            </div>
                          ))}
                          <button type="button" className="btn btn-outline btn-sm add-option-btn" onClick={() => addOption(qi)}>+ Add Option</button>
                        </div>
                      )}
                    </div>
                  ))}
                  {questions.length < 20 && (
                    <button type="button" className="btn btn-outline btn-sm" onClick={addQuestion} style={{ width: '100%' }}>
                      + Add Question ({questions.length}/20)
                    </button>
                  )}
                </div>
              )}

              <div className="modal-actions">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-orange" disabled={saving}>{saving ? 'Saving...' : (editing ? 'Update' : 'Add Lesson')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}
